import { normalizeModifierUuidList } from '@/models/MenuItemModifier.js'
import { isVariantSkuPubliclyOrderable } from '@/utils/publicVariantSku.js'

/**
 * Normalized modifier groups from a public menu item (matches detail modal / API).
 * Legacy flat `modifiers` is treated as one multiple-choice group (optional picks).
 *
 * @returns {Array<{ uuid: string, name: string, selection_type: 'single' | 'multiple', options: object[] }>}
 */
export function getPublicMenuItemModifierGroups(item) {
  if (Array.isArray(item?.modifier_groups) && item.modifier_groups.length) {
    return item.modifier_groups.map((g) => ({
      uuid: g.uuid ?? '',
      name: g.name ?? '',
      selection_type: g.selection_type === 'multiple' ? 'multiple' : 'single',
      options: Array.isArray(g.options) ? g.options : [],
    }))
  }
  if (Array.isArray(item?.modifiers) && item.modifiers.length) {
    return [
      {
        uuid: 'legacy',
        name: '',
        selection_type: 'multiple',
        options: item.modifiers,
      },
    ]
  }
  return []
}

/**
 * Single-selection (radio) groups require exactly one chosen option from that group.
 * Multiple-selection groups do not require any selection.
 */
export function areRequiredSingleChoiceModifiersSatisfied(item, modifierUuids) {
  const selected = new Set(normalizeModifierUuidList(modifierUuids))
  for (const g of getPublicMenuItemModifierGroups(item)) {
    if (g.selection_type === 'multiple') continue
    const opts = (g.options || []).filter((o) => o?.uuid)
    if (opts.length === 0) continue
    if (!opts.some((o) => selected.has(String(o.uuid)))) return false
  }
  return true
}

/**
 * Whether a persisted cart line still matches the current public menu (visible item, orderable variant, allowed modifiers).
 * @param {object} line - { menu_item_uuid, variant_uuid?, modifier_uuids? }
 * @param {object|null|undefined} item - Current public menu item payload or null if removed from menu
 */
export function isPublicMenuCartLineValid(line, item) {
  if (!line?.menu_item_uuid || !item) return false
  if (item.is_available === false || item.isAvailable === false) return false

  const variantSkus = Array.isArray(item.variant_skus) ? item.variant_skus : []
  const isWithVariants = item.type === 'with_variants' || variantSkus.length > 0
  const lineVariant = line.variant_uuid != null && String(line.variant_uuid).trim() !== '' ? String(line.variant_uuid) : ''

  if (!isWithVariants && lineVariant !== '') return false

  if (isWithVariants) {
    if (lineVariant === '') return false
    const sku = variantSkus.find((s) => String(s.uuid ?? '') === lineVariant)
    if (!sku || !isVariantSkuPubliclyOrderable(sku)) return false
  }

  const allowedModUuids = new Set(
    getPublicMenuItemModifierGroups(item).flatMap((g) =>
      (g.options ?? []).map((o) => String(o.uuid ?? '')).filter(Boolean),
    ),
  )
  for (const m of normalizeModifierUuidList(line.modifier_uuids)) {
    if (!allowedModUuids.has(String(m))) return false
  }

  return true
}

export function variantDisplayNameForCart(item, sku) {
  if (!sku?.option_values || typeof sku.option_values !== 'object') return item?.name ?? ''
  const parts = Object.values(sku.option_values).filter(Boolean)
  return parts.length ? `${item?.name ?? ''} – ${parts.join(', ')}` : (item?.name ?? '')
}

/**
 * @param {object} params
 * @param {object} params.item - Public menu item (uuid, name, price, modifiers?, type)
 * @param {object|null} params.selectedSku - Variant SKU when applicable
 * @param {string[]} params.modifier_uuids - Selected modifier UUIDs (any order; normalized when matching cart)
 */
function publicItemModifierOptions(item) {
  return getPublicMenuItemModifierGroups(item).flatMap((g) => g.options ?? [])
}

export function buildQuickOrderCartLinePayload({ item, selectedSku, modifier_uuids }) {
  const mods = publicItemModifierOptions(item)
  const sorted = normalizeModifierUuidList(modifier_uuids)
  const selected = mods.filter((m) => m?.uuid && sorted.includes(String(m.uuid)))
  const base =
    selectedSku?.price != null
      ? Number(selectedSku.price)
      : item?.price != null
        ? Number(item.price)
        : null
  const delta = selected.reduce((sum, m) => sum + (Number(m.price_delta) || 0), 0)
  const price = base != null && !Number.isNaN(base) ? base + delta : null
  let name = selectedSku ? variantDisplayNameForCart(item, selectedSku) : (item?.name ?? '')
  if (selected.length) {
    const labels = selected.map((m) => (m.name ?? '').trim()).filter(Boolean)
    if (labels.length) name = `${name} (+ ${labels.join(', ')})`
  }
  return {
    menu_item_uuid: item.uuid,
    variant_uuid: selectedSku?.uuid ?? null,
    modifier_uuids: sorted,
    name,
    price,
    image_url: selectedSku?.image_url ?? item?.image_url ?? null,
  }
}
